/* eslint-disable @typescript-eslint/no-explicit-any*/
import React, { useEffect } from 'react'

// Performance metrics interface
export interface PerformanceMetrics {
  lcp?: number
  fid?: number
  cls?: number
  tbt?: number
  tti?: number
  hydrationTime?: number
  componentRenderTime?: number
  bundleSize?: number
  timestamp?: number
  url?: string
  userAgent?: string
  connectionType?: string
  deviceMemory?: number
}

// Real User Metrics (RUM) data structure
export interface RUMData {
  sessionId: string
  userId?: string
  metrics: PerformanceMetrics
  pageInfo: {
    url: string
    title: string
    referrer: string
  }
  deviceInfo: {
    userAgent: string
    viewport: { width: number; height: number }
    connectionType?: string
    deviceMemory?: number
  }
  timestamp: number
}

// Performance regression data
export interface PerformanceRegression {
  metric: string
  currentValue: number
  previousValue: number
  threshold: number
  severity: 'low' | 'medium' | 'high'
  timestamp: number
  url: string
}

// Alert configuration
export interface AlertConfig {
  enabled: boolean
  thresholds: {
    [key: string]: {
      warning: number
      critical: number
    }
  }
  webhookUrl?: string
  emailRecipients?: string[]
}

// Performance budget thresholds
export const PERFORMANCE_BUDGETS = {
  LCP: 2500, // ms on 3G
  CLS: 0.1,
  TBT: 150, // ms
  FID: 100, // ms
  TTI: 3500, // ms on 3G
  BUNDLE_SIZE: 250000, // bytes for critical JS
  HYDRATION_TIME: 1000, // ms
  COMPONENT_RENDER_TIME: 16 // ms (one frame at 60fps)
} as const

// Performance monitoring class
export class PerformanceMonitor {
  private metrics: PerformanceMetrics = {}
  private observers: PerformanceObserver[] = []
  private sessionId: string
  private rumData: RUMData[] = []
  private baselineMetrics: Map<string, number> = new Map()
  private alertConfig: AlertConfig
  private isInitialized = false

  constructor(alertConfig?: Partial<AlertConfig>) {
    this.sessionId = this.generateSessionId()
    this.alertConfig = {
      enabled: true,
      thresholds: {
        LCP: { warning: 2000, critical: 2500 },
        CLS: { warning: 0.05, critical: 0.1 },
        FID: { warning: 50, critical: 100 },
        TBT: { warning: 100, critical: 150 }
      },
      ...alertConfig
    }

    if (typeof window !== 'undefined') {
      this.initializeObservers()
      this.loadBaselineMetrics()
      this.isInitialized = true
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private initializeObservers() {
    // Core Web Vitals observer
    if ('PerformanceObserver' in window) {
      try {
        // LCP Observer
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1] as any
          this.metrics.lcp = lastEntry.startTime
          this.checkBudget('LCP', lastEntry.startTime)
          this.collectRUMData('LCP', lastEntry.startTime)
        })
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
        this.observers.push(lcpObserver)

        // CLS Observer
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value
            }
          }
          this.metrics.cls = clsValue
          this.checkBudget('CLS', clsValue)
          this.collectRUMData('CLS', clsValue)
        })
        clsObserver.observe({ entryTypes: ['layout-shift'] })
        this.observers.push(clsObserver)

        // FID Observer
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.metrics.fid = (entry as any).processingStart - entry.startTime
            this.checkBudget('FID', this.metrics.fid)
            this.collectRUMData('FID', this.metrics.fid)
          }
        })
        fidObserver.observe({ entryTypes: ['first-input'] })
        this.observers.push(fidObserver)

        // Long Task Observer for TBT calculation
        const longTaskObserver = new PerformanceObserver(() => {
          // Recalculate TBT when new long tasks are detected
          setTimeout(() => this.calculateTBT(), 0)
        })
        longTaskObserver.observe({ entryTypes: ['longtask'] })
        this.observers.push(longTaskObserver)

      } catch (error) {
        console.warn('Performance observer initialization failed:', error)
      }
    }

    // Set up page visibility change listener for RUM data collection
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.sendRUMData()
      }
    })

    // Send RUM data before page unload
    window.addEventListener('beforeunload', () => {
      this.sendRUMData()
    })
  }

  private loadBaselineMetrics() {
    try {
      const stored = localStorage.getItem('performance-baselines')
      if (stored) {
        const baselines = JSON.parse(stored)
        this.baselineMetrics = new Map(Object.entries(baselines))
      }
    } catch (error) {
      console.warn('Failed to load baseline metrics:', error)
    }
  }

  private saveBaselineMetrics() {
    try {
      const baselines = Object.fromEntries(this.baselineMetrics)
      localStorage.setItem('performance-baselines', JSON.stringify(baselines))
    } catch (error) {
      console.warn('Failed to save baseline metrics:', error)
    }
  }

  private collectRUMData(metric: string, value: number) {
    if (!this.isInitialized) return

    const rumEntry: RUMData = {
      sessionId: this.sessionId,
      metrics: {
        ...this.metrics,
        [metric.toLowerCase()]: value,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        connectionType: this.getConnectionType(),
        deviceMemory: this.getDeviceMemory()
      },
      pageInfo: {
        url: window.location.href,
        title: document.title,
        referrer: document.referrer
      },
      deviceInfo: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        connectionType: this.getConnectionType(),
        deviceMemory: this.getDeviceMemory()
      },
      timestamp: Date.now()
    }

    this.rumData.push(rumEntry)

    // Check for regressions
    this.checkForRegression(metric, value)
  }

  private getConnectionType(): string | undefined {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
    return connection?.effectiveType
  }

  private getDeviceMemory(): number | undefined {
    return (navigator as any).deviceMemory
  }

  private checkForRegression(metric: string, currentValue: number) {
    const baselineKey = `${metric}_${window.location.pathname}`
    const baseline = this.baselineMetrics.get(baselineKey)

    if (baseline) {
      const threshold = this.getRegressionThreshold(metric)
      const percentageChange = ((currentValue - baseline) / baseline) * 100

      if (percentageChange > threshold) {
        const regression: PerformanceRegression = {
          metric,
          currentValue,
          previousValue: baseline,
          threshold,
          severity: this.getRegressionSeverity(percentageChange),
          timestamp: Date.now(),
          url: window.location.href
        }

        this.handleRegression(regression)
      }
    } else {
      // Set as new baseline
      this.baselineMetrics.set(baselineKey, currentValue)
      this.saveBaselineMetrics()
    }
  }

  private getRegressionThreshold(metric: string): number {
    const thresholds: Record<string, number> = {
      LCP: 10, // 10% increase
      CLS: 20, // 20% increase
      FID: 15, // 15% increase
      TBT: 15  // 15% increase
    }
    return thresholds[metric] || 10
  }

  private getRegressionSeverity(percentageChange: number): 'low' | 'medium' | 'high' {
    if (percentageChange > 50) return 'high'
    if (percentageChange > 25) return 'medium'
    return 'low'
  }

  private handleRegression(regression: PerformanceRegression) {
    console.warn('[Performance Regression Detected]', regression)

    // Emit custom event for external monitoring systems
    window.dispatchEvent(new CustomEvent('performance-regression', {
      detail: regression
    }))

    // Send alert if configured
    if (this.alertConfig.enabled) {
      this.sendAlert(regression)
    }
  }

  private async sendAlert(regression: PerformanceRegression) {
    if (this.alertConfig.webhookUrl) {
      try {
        await fetch(this.alertConfig.webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: 'performance_regression',
            data: regression,
            timestamp: new Date().toISOString()
          })
        })
      } catch (error) {
        console.error('Failed to send performance alert:', error)
      }
    }
  }

  private async sendRUMData() {
    if (this.rumData.length === 0) return

    try {
      // Send to analytics endpoint
      await fetch('/api/analytics/performance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          data: this.rumData
        }),
        keepalive: true
      })

      // Clear sent data
      this.rumData = []
    } catch (error) {
      console.warn('Failed to send RUM data:', error)
    }
  }

  // Measure hydration time
  measureHydrationTime(componentName: string, startTime: number) {
    const endTime = performance.now()
    const hydrationTime = endTime - startTime
    
    this.metrics.hydrationTime = hydrationTime
    this.checkBudget('HYDRATION_TIME', hydrationTime)
    
    console.log(`[Performance] ${componentName} hydration time: ${hydrationTime.toFixed(2)}ms`)
    
    return hydrationTime
  }

  // Measure component render time
  measureComponentRender(componentName: string, renderFn: () => void) {
    const startTime = performance.now()
    renderFn()
    const endTime = performance.now()
    const renderTime = endTime - startTime
    
    this.metrics.componentRenderTime = renderTime
    this.checkBudget('COMPONENT_RENDER_TIME', renderTime)
    
    if (renderTime > PERFORMANCE_BUDGETS.COMPONENT_RENDER_TIME) {
      console.warn(`[Performance] ${componentName} render time exceeded budget: ${renderTime.toFixed(2)}ms`)
    }
    
    return renderTime
  }

  // Check if metric exceeds budget
  private checkBudget(metric: keyof typeof PERFORMANCE_BUDGETS, value: number) {
    const budget = PERFORMANCE_BUDGETS[metric]
    const alertThresholds = this.alertConfig.thresholds[metric]
    
    if (value > budget) {
      console.warn(`[Performance Budget] ${metric} exceeded: ${value.toFixed(2)} > ${budget}`)
      
      // Emit custom event for monitoring systems
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('performance-budget-exceeded', {
          detail: { metric, value, budget, severity: this.getBudgetSeverity(metric, value) }
        }))
      }

      // Send alert if configured and threshold exceeded
      if (this.alertConfig.enabled && alertThresholds) {
        if (value > alertThresholds.critical) {
          this.sendBudgetAlert(metric, value, 'critical')
        } else if (value > alertThresholds.warning) {
          this.sendBudgetAlert(metric, value, 'warning')
        }
      }
    }
  }

  private getBudgetSeverity(metric: string, value: number): 'low' | 'medium' | 'high' {
    const budget = PERFORMANCE_BUDGETS[metric as keyof typeof PERFORMANCE_BUDGETS]
    const ratio = value / budget
    
    if (ratio > 2) return 'high'
    if (ratio > 1.5) return 'medium'
    return 'low'
  }

  private async sendBudgetAlert(metric: string, value: number, severity: 'warning' | 'critical') {
    const alert = {
      type: 'performance_budget_exceeded',
      metric,
      value,
      budget: PERFORMANCE_BUDGETS[metric as keyof typeof PERFORMANCE_BUDGETS],
      severity,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId
    }

    if (this.alertConfig.webhookUrl) {
      try {
        await fetch(this.alertConfig.webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(alert)
        })
      } catch (error) {
        console.error('Failed to send budget alert:', error)
      }
    }
  }

  // Get current metrics
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics }
  }

  // Calculate Total Blocking Time (TBT)
  calculateTBT(): number {
    if (!('performance' in window)) return 0

    const navigationEntry = performance.getEntriesByType('navigation')[0] as any
    if (!navigationEntry) return 0

    const fcpTime = navigationEntry.loadEventEnd
    const ttiTime = this.estimateTTI()
    
    const longTasks = performance.getEntriesByType('longtask') as any[]
    let tbt = 0

    longTasks.forEach((task) => {
      if (task.startTime >= fcpTime && task.startTime < ttiTime) {
        const blockingTime = Math.max(0, task.duration - 50) // 50ms threshold
        tbt += blockingTime
      }
    })

    this.metrics.tbt = tbt
    this.checkBudget('TBT', tbt)
    
    return tbt
  }

  // Estimate Time to Interactive (simplified)
  private estimateTTI(): number {
    if (!('performance' in window)) return 0

    const navigationEntry = performance.getEntriesByType('navigation')[0] as any
    if (!navigationEntry) return 0

    // Simplified TTI estimation - in practice, you'd use a more sophisticated algorithm
    return navigationEntry.loadEventEnd + 1000 // Rough estimate
  }

  // Monitor bundle size impact
  monitorBundleSize() {
    if (!('performance' in window)) return

    const resourceEntries = performance.getEntriesByType('resource') as any[]
    let totalJSSize = 0

    resourceEntries.forEach((entry) => {
      if (entry.name.includes('.js') && !entry.name.includes('chunk')) {
        totalJSSize += entry.transferSize || 0
      }
    })

    this.metrics.bundleSize = totalJSSize
    this.checkBudget('BUNDLE_SIZE', totalJSSize)
    
    return totalJSSize
  }

  // Generate performance report
  generateReport(): string {
    const metrics = this.getMetrics()
    const report = []

    report.push('=== Performance Report ===')
    report.push(`Session ID: ${this.sessionId}`)
    report.push(`URL: ${window.location.href}`)
    report.push(`Timestamp: ${new Date().toISOString()}`)
    report.push('')
    
    if (metrics.lcp) {
      const status = metrics.lcp <= PERFORMANCE_BUDGETS.LCP ? '✅' : '❌'
      const severity = this.getBudgetSeverity('LCP', metrics.lcp)
      report.push(`${status} LCP: ${metrics.lcp.toFixed(2)}ms (budget: ${PERFORMANCE_BUDGETS.LCP}ms) [${severity}]`)
    }
    
    if (metrics.cls !== undefined) {
      const status = metrics.cls <= PERFORMANCE_BUDGETS.CLS ? '✅' : '❌'
      const severity = this.getBudgetSeverity('CLS', metrics.cls)
      report.push(`${status} CLS: ${metrics.cls.toFixed(3)} (budget: ${PERFORMANCE_BUDGETS.CLS}) [${severity}]`)
    }
    
    if (metrics.fid) {
      const status = metrics.fid <= PERFORMANCE_BUDGETS.FID ? '✅' : '❌'
      const severity = this.getBudgetSeverity('FID', metrics.fid)
      report.push(`${status} FID: ${metrics.fid.toFixed(2)}ms (budget: ${PERFORMANCE_BUDGETS.FID}ms) [${severity}]`)
    }
    
    if (metrics.tbt) {
      const status = metrics.tbt <= PERFORMANCE_BUDGETS.TBT ? '✅' : '❌'
      const severity = this.getBudgetSeverity('TBT', metrics.tbt)
      report.push(`${status} TBT: ${metrics.tbt.toFixed(2)}ms (budget: ${PERFORMANCE_BUDGETS.TBT}ms) [${severity}]`)
    }
    
    if (metrics.hydrationTime) {
      const status = metrics.hydrationTime <= PERFORMANCE_BUDGETS.HYDRATION_TIME ? '✅' : '❌'
      report.push(`${status} Hydration: ${metrics.hydrationTime.toFixed(2)}ms (budget: ${PERFORMANCE_BUDGETS.HYDRATION_TIME}ms)`)
    }
    
    if (metrics.bundleSize) {
      const status = metrics.bundleSize <= PERFORMANCE_BUDGETS.BUNDLE_SIZE ? '✅' : '❌'
      const sizeKB = (metrics.bundleSize / 1024).toFixed(2)
      const budgetKB = (PERFORMANCE_BUDGETS.BUNDLE_SIZE / 1024).toFixed(2)
      report.push(`${status} Bundle Size: ${sizeKB}KB (budget: ${budgetKB}KB)`)
    }

    // Add device and connection info
    if (metrics.connectionType) {
      report.push(`Connection: ${metrics.connectionType}`)
    }
    if (metrics.deviceMemory) {
      report.push(`Device Memory: ${metrics.deviceMemory}GB`)
    }

    return report.join('\n')
  }

  // Generate dashboard data for monitoring UI
  generateDashboardData() {
    const metrics = this.getMetrics()
    const now = Date.now()
    
    return {
      sessionId: this.sessionId,
      timestamp: now,
      coreWebVitals: {
        lcp: {
          value: metrics.lcp || 0,
          budget: PERFORMANCE_BUDGETS.LCP,
          status: metrics.lcp ? (metrics.lcp <= PERFORMANCE_BUDGETS.LCP ? 'pass' : 'fail') : 'pending',
          severity: metrics.lcp ? this.getBudgetSeverity('LCP', metrics.lcp) : 'low'
        },
        cls: {
          value: metrics.cls || 0,
          budget: PERFORMANCE_BUDGETS.CLS,
          status: metrics.cls !== undefined ? (metrics.cls <= PERFORMANCE_BUDGETS.CLS ? 'pass' : 'fail') : 'pending',
          severity: metrics.cls !== undefined ? this.getBudgetSeverity('CLS', metrics.cls) : 'low'
        },
        fid: {
          value: metrics.fid || 0,
          budget: PERFORMANCE_BUDGETS.FID,
          status: metrics.fid ? (metrics.fid <= PERFORMANCE_BUDGETS.FID ? 'pass' : 'fail') : 'pending',
          severity: metrics.fid ? this.getBudgetSeverity('FID', metrics.fid) : 'low'
        },
        tbt: {
          value: metrics.tbt || 0,
          budget: PERFORMANCE_BUDGETS.TBT,
          status: metrics.tbt ? (metrics.tbt <= PERFORMANCE_BUDGETS.TBT ? 'pass' : 'fail') : 'pending',
          severity: metrics.tbt ? this.getBudgetSeverity('TBT', metrics.tbt) : 'low'
        }
      },
      customMetrics: {
        hydrationTime: metrics.hydrationTime || 0,
        componentRenderTime: metrics.componentRenderTime || 0,
        bundleSize: metrics.bundleSize || 0
      },
      deviceInfo: {
        userAgent: metrics.userAgent || navigator.userAgent,
        connectionType: metrics.connectionType || this.getConnectionType(),
        deviceMemory: metrics.deviceMemory || this.getDeviceMemory(),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      },
      pageInfo: {
        url: window.location.href,
        title: document.title,
        referrer: document.referrer
      },
      regressions: this.getRecentRegressions(),
      rumDataCount: this.rumData.length
    }
  }

  // Get recent performance regressions
  private getRecentRegressions(): PerformanceRegression[] {
    // In a real implementation, this would fetch from a data store
    // For now, we'll return any regressions detected in this session
    return [] // Placeholder - would be populated by regression detection
  }

  // Export RUM data for analysis
  exportRUMData(): RUMData[] {
    return [...this.rumData]
  }

  // Get performance score (0-100)
  getPerformanceScore(): number {
    const metrics = this.getMetrics()
    let score = 100
    let totalWeight = 0

    // LCP scoring (weight: 25%)
    if (metrics.lcp) {
      const lcpScore = Math.max(0, 100 - ((metrics.lcp - 1200) / 1300) * 100)
      score += lcpScore * 0.25
      totalWeight += 0.25
    }

    // CLS scoring (weight: 25%)
    if (metrics.cls !== undefined) {
      const clsScore = Math.max(0, 100 - (metrics.cls / 0.25) * 100)
      score += clsScore * 0.25
      totalWeight += 0.25
    }

    // FID scoring (weight: 25%)
    if (metrics.fid) {
      const fidScore = Math.max(0, 100 - ((metrics.fid - 100) / 200) * 100)
      score += fidScore * 0.25
      totalWeight += 0.25
    }

    // TBT scoring (weight: 25%)
    if (metrics.tbt) {
      const tbtScore = Math.max(0, 100 - ((metrics.tbt - 200) / 400) * 100)
      score += tbtScore * 0.25
      totalWeight += 0.25
    }

    return totalWeight > 0 ? Math.round(score / totalWeight) : 0
  }

  // Update alert configuration
  updateAlertConfig(config: Partial<AlertConfig>) {
    this.alertConfig = { ...this.alertConfig, ...config }
  }

  // Manual baseline update
  updateBaseline(metric: string, value: number, url?: string) {
    const key = `${metric}_${url || window.location.pathname}`
    this.baselineMetrics.set(key, value)
    this.saveBaselineMetrics()
  }

  // Clear all baselines (useful for testing)
  clearBaselines() {
    this.baselineMetrics.clear()
    localStorage.removeItem('performance-baselines')
  }

  // Clean up observers
  disconnect() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }
}

// Global performance monitor instance
let globalMonitor: PerformanceMonitor | null = null

export function getPerformanceMonitor(): PerformanceMonitor {
  if (!globalMonitor) {
    globalMonitor = new PerformanceMonitor()
  }
  return globalMonitor
}

// Hook for React components to use performance monitoring
export function usePerformanceMonitoring(componentName: string) {
  const monitor = getPerformanceMonitor()
  
  return {
    measureRender: (renderFn: () => void) => monitor.measureComponentRender(componentName, renderFn),
    measureHydration: (startTime: number) => monitor.measureHydrationTime(componentName, startTime),
    getMetrics: () => monitor.getMetrics(),
    generateReport: () => monitor.generateReport()
  }
}

// Utility to wrap components with performance monitoring
export function withPerformanceMonitoring<T extends {object: any}>(
  Component: React.ComponentType<T>,
  componentName: string
) {
  return function PerformanceMonitoredComponent(props: T) {
    useEffect(() => {
      const startTime = performance.now()
      
      return () => {
        const endTime = performance.now()
        const renderTime = endTime - startTime
        
        // Only log if render time is significant
        if (renderTime > 16) { // More than one frame at 60fps
          console.log(`[Performance] ${componentName} render time: ${renderTime.toFixed(2)}ms`)
        }
      }
    })

    return React.createElement(Component, props)
  }
}

// Development-only performance logging
export function logPerformanceMetrics() {
  if (process.env.NODE_ENV === 'development') {
    const monitor = getPerformanceMonitor()
    console.log(monitor.generateReport())
  }
}